/* AURIQ Adaptive Tracker
 * Loaded on every storefront page via the auriq-tracker app embed block.
 * Optional behavior waits for Shopify consent; the storefront never blocks.
 */
(function () {
  "use strict";

  var cfg = window.AuriqConfig;
  if (!cfg || !cfg.proxyUrl) return;
  if (window.__auriqTrackerBooted) return;
  window.__auriqTrackerBooted = true;

  // ---- storage keys ----------------------------------------------------
  var KEY_VID = "auriq_vid";
  var KEY_HMAC = "auriq_vhmac";
  var KEY_SID = "auriq_sid";
  var KEY_PENDING = "auriq_pending";
  var ISSUE_BACKOFF_KEY = "auriq_issue_backoff_until";
  var TEST_SESSION_KEY = "auriq.test-session.v1";
  var PROBE_SEEN_PREFIX = "auriq_probe_seen_";
  var TEST_PROBE_SEEN_PREFIX = "auriq.test-probe-seen.";
  var UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  // ---- tiny safe helpers ----------------------------------------------
  function safeGet(store, k) { try { return store.getItem(k); } catch (e) { return null; } }
  function safeSet(store, k, v) { try { store.setItem(k, v); return true; } catch (e) { return false; } }
  function safeRemove(store, k) { try { store.removeItem(k); return true; } catch (e) { return false; } }

  function testSessionState() {
    var raw = safeGet(sessionStorage, TEST_SESSION_KEY);
    if (raw === null) return { present: false, session: null };
    try {
      var value = JSON.parse(raw);
      var valid = value && typeof value === "object" && !Array.isArray(value)
        && UUID_RE.test(value.id || "") && /^[A-Za-z0-9_-]{22}$/.test(value.hmac || "")
        && UUID_RE.test(value.sessionId || "") && value.shop === cfg.shopDomain
        && value.origin === window.location.origin;
      return { present: true, session: valid ? value : null };
    } catch (e) {
      return { present: true, session: null };
    }
  }

  // RFC 4122 UUIDv4 (sessionStorage session id is fine as v4 — we only need
  // randomness, not time-ordering, here)
  function uuidv4() {
    if (window.crypto && crypto.getRandomValues) {
      var b = new Uint8Array(16);
      crypto.getRandomValues(b);
      b[6] = (b[6] & 0x0f) | 0x40;
      b[8] = (b[8] & 0x3f) | 0x80;
      var h = [];
      for (var i = 0; i < b.length; i++) h.push((b[i] + 0x100).toString(16).slice(1));
      return h[0]+h[1]+h[2]+h[3]+"-"+h[4]+h[5]+"-"+h[6]+h[7]+"-"+h[8]+h[9]+"-"+h[10]+h[11]+h[12]+h[13]+h[14]+h[15];
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0, v = c === "x" ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  function setFirstPartyCookie(name, value) {
    try {
      var host = window.location.hostname;
      // Best-effort cookie domain: strip leading "www." and any subdomain
      // beyond two labels so .auriq.jewelry takes the apex. On myshopify.com
      // dev domains this won't stick — we rely on localStorage instead.
      var parts = host.split(".");
      var domain = parts.length >= 2 ? "." + parts.slice(-2).join(".") : host;
      var twoYears = 60 * 60 * 24 * 730;
      document.cookie = name + "=" + encodeURIComponent(value)
        + "; Max-Age=" + twoYears
        + "; Path=/"
        + "; Domain=" + domain
        + "; SameSite=Lax; Secure";
      return true;
    } catch (e) { return false; }
  }

  // ---- visitor id management ------------------------------------------
  function loadVisitor() {
    var test = testSessionState();
    if (test.present) return test.session ? { id: test.session.id, hmac: test.session.hmac } : null;
    var id = safeGet(localStorage, KEY_VID);
    var hmac = safeGet(localStorage, KEY_HMAC);
    return id && hmac ? { id: id, hmac: hmac } : null;
  }

  function saveVisitor(id, hmac) {
    safeSet(localStorage, KEY_VID, id);
    safeSet(localStorage, KEY_HMAC, hmac);
    setFirstPartyCookie(KEY_VID, id);
  }

  // session id (sessionStorage, fresh per tab)
  function sessionId() {
    var test = testSessionState();
    if (test.present) return test.session ? test.session.sessionId : "";
    var s = safeGet(sessionStorage, KEY_SID);
    if (s) return s;
    s = uuidv4();
    safeSet(sessionStorage, KEY_SID, s);
    return s;
  }

  // ---- issue-visitor with backoff -------------------------------------
  var ISSUE_BACKOFF_STEPS_MS = [1000, 5000, 30000, 300000];
  var issueAttempts = 0;
  var issuePromise = null;
  var operationalIdentityNeeded = false;

  function nextBackoff() {
    var step = ISSUE_BACKOFF_STEPS_MS[Math.min(issueAttempts, ISSUE_BACKOFF_STEPS_MS.length - 1)];
    issueAttempts++;
    return Date.now() + step;
  }

  function tryIssueVisitor() {
    var test = testSessionState();
    if (test.present) return Promise.resolve(test.session ? { id: test.session.id, hmac: test.session.hmac } : null);
    var existing = loadVisitor();
    if (existing) return Promise.resolve(existing);
    if (issuePromise) return issuePromise;
    var until = parseInt(safeGet(localStorage, ISSUE_BACKOFF_KEY) || "0", 10);
    if (until > Date.now()) return Promise.resolve(null);

    issuePromise = fetch(cfg.proxyUrl + "/issue-visitor", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: "{}"
    }).then(function (r) {
      if (!r.ok) throw new Error("issue " + r.status);
      return r.json();
    }).then(function (data) {
      if (!data || !data.visitor_id || !data.visitor_hmac) throw new Error("issue malformed");
      issueAttempts = 0;
      safeRemove(localStorage, ISSUE_BACKOFF_KEY);
      if (!analyticsAllowed && !operationalIdentityNeeded) return null;
      saveVisitor(data.visitor_id, data.visitor_hmac);
      return data;
    }).catch(function () {
      safeSet(localStorage, ISSUE_BACKOFF_KEY, String(nextBackoff()));
      return null;
    }).then(function (result) {
      issuePromise = null;
      return result;
    });
    return issuePromise;
  }

  // Issue an operational guest identity only after an explicit storefront
  // operation asks for one, or while consented analytics is being sent.
  function ensureGuestIdentity() {
    operationalIdentityNeeded = true;
    return tryIssueVisitor().then(function (visitor) {
      if (!visitor) return null;
      var id = visitor.id || visitor.visitor_id;
      var hmac = visitor.hmac || visitor.visitor_hmac;
      if (!id || !hmac) return null;
      var stored = loadVisitor();
      return stored && stored.id === id && stored.hmac === hmac ? { id: id, hmac: hmac } : null;
    }).then(function (visitor) {
      operationalIdentityNeeded = false;
      return visitor;
    }, function () {
      operationalIdentityNeeded = false;
      return null;
    });
  }

  // ---- event queue + flush --------------------------------------------
  var queue = [];
  // Old versions buffered events before consent; never replay that history.
  if (!testSessionState().present) safeRemove(localStorage, KEY_PENDING);
  var flushTimer = null;
  var FLUSH_EVERY_MS = 5000;
  var FLUSH_SIZE = 20;
  var tempVisitorId = "tmp_" + uuidv4();
  var analyticsAllowed = false;
  var marketingAllowed = false;
  var privacyInitialized = false;
  var behaviorActive = false;
  var pageOpenedAt = 0;
  var probesRequested = false;
  var customerIdentified = false;
  var attributionCaptured = false;

  function currentPath() {
    try { return window.location.pathname.slice(0, 2048) || "/"; } catch (e) { return "/"; }
  }

  function clearBehavioralState() {
    var test = testSessionState();
    queue.length = 0;
    if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
    if (test.present) {
      var suffix = test.session ? test.session.sessionId : "unavailable";
      safeRemove(sessionStorage, sessionScopedKey("auriq_session_pv", suffix));
      safeRemove(sessionStorage, sessionScopedKey("auriq_session_milestone_fired", suffix));
      try {
        var probePrefix = TEST_PROBE_SEEN_PREFIX + suffix + ".";
        for (var i = sessionStorage.length - 1; i >= 0; i--) {
          var testKey = sessionStorage.key(i);
          if (testKey && testKey.indexOf(probePrefix) === 0) safeRemove(sessionStorage, testKey);
        }
      } catch (e) {
        // Storage may be disabled; the in-memory queue and visible probe UI are still cleared below.
      }
    } else {
      safeRemove(localStorage, KEY_PENDING);
      safeRemove(sessionStorage, KEY_SID);
      safeRemove(sessionStorage, "auriq_session_pv");
      safeRemove(sessionStorage, "auriq_session_milestone_fired");
      try {
        for (var j = localStorage.length - 1; j >= 0; j--) {
          var key = localStorage.key(j);
          if (key && key.indexOf(PROBE_SEEN_PREFIX) === 0) safeRemove(localStorage, key);
        }
      } catch (e) {
        // Storage may be disabled; the in-memory queue and visible probe UI are still cleared below.
      }
    }
    var probes = document.querySelectorAll("[data-auriq-probe]");
    for (var probeIndex = 0; probeIndex < probes.length; probeIndex++) probes[probeIndex].remove();
  }

  function scheduleFlush() {
    if (!analyticsAllowed) return;
    if (flushTimer) return;
    flushTimer = setTimeout(function () {
      flushTimer = null;
      flush(false);
    }, FLUSH_EVERY_MS);
  }

  function pushEvent(type, props) {
    if (!analyticsAllowed || !type) return;
    queue.push({
      session_id: sessionId(),
      ts_ms: Date.now(),
      type: type,
      page_url: currentPath(),
      props: props || {}
    });
    if (queue.length >= FLUSH_SIZE) flush(false);
    else scheduleFlush();
  }

  function flush(useBeacon) {
    if (!analyticsAllowed || queue.length === 0) return;
    var visitor = loadVisitor();
    var rebindFrom = null;
    if (!visitor) {
      tryIssueVisitor().then(function (issued) {
        if (issued && analyticsAllowed) {
          flush(false);
          initializeConsentFeatures();
        }
      });
      return;
    }
    if (tempVisitorId && !testSessionState().present) {
      rebindFrom = tempVisitorId;
      tempVisitorId = null;
    }
    var batch = queue.splice(0, FLUSH_SIZE);
    var payload = {
      visitor_id: visitor.id,
      visitor_hmac: visitor.hmac,
      events: batch
    };
    if (rebindFrom) payload.rebind_from_visitor_id = rebindFrom;

    var url = cfg.proxyUrl + "/track";
    if (useBeacon && navigator.sendBeacon) {
      try {
        var blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
        var ok = navigator.sendBeacon(url, blob);
        if (!ok && analyticsAllowed) { queue = batch.concat(queue).slice(-200); scheduleFlush(); }
        return;
      } catch (e) {
        if (analyticsAllowed) { queue = batch.concat(queue).slice(-200); scheduleFlush(); }
        return;
      }
    }

    fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true
    }).catch(function () {
      if (analyticsAllowed) { queue = batch.concat(queue).slice(-200); scheduleFlush(); }
    });
  }

  // ---- identify -------------------------------------------------------
  function identify(kind, value) {
    if (!analyticsAllowed || !marketingAllowed || !kind || !value) return;
    var visitor = loadVisitor();
    if (!visitor) return;
    fetch(cfg.proxyUrl + "/identify", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        visitor_id: visitor.id,
        visitor_hmac: visitor.hmac,
        kind: kind,
        value: value,
        confidence: 100
      })
    }).catch(function () {});
  }

  // ---- auto-track ------------------------------------------------------
  var SESSION_PV_KEY = "auriq_session_pv";
  var SESSION_MILESTONE_KEY = "auriq_session_milestone_fired";
  var SESSION_MILESTONE_PV = 5;

  function sessionScopedKey(key, testSessionId) {
    if (testSessionId !== undefined) return key + ":test:" + testSessionId;
    var test = testSessionState();
    return test.present ? key + ":test:" + (test.session ? test.session.sessionId : "unavailable") : key;
  }

  function readMarketingAttribution() {
    if (!marketingAllowed) return {};
    var attribution = {};
    try {
      var params = new URLSearchParams(window.location.search);
      ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "gbraid", "wbraid"].forEach(function (key) {
        var value = params.get(key);
        if (value) attribution[key] = value.slice(0, 512);
      });
    } catch (e) { return {}; }
    return attribution;
  }

  function trackMarketingAttributionOnce() {
    if (attributionCaptured || !analyticsAllowed || !marketingAllowed) return;
    attributionCaptured = true;
    var attribution = readMarketingAttribution();
    if (Object.keys(attribution).length === 0) return;
    var props = { title: document.title ? document.title.slice(0, 512) : undefined };
    Object.keys(attribution).forEach(function (key) { props[key] = attribution[key]; });
    pushEvent("page_view", props);
    flush(false);
  }

  function trackPageView(captureAttribution) {
    var props = { title: document.title ? document.title.slice(0, 512) : undefined };
    if (captureAttribution && marketingAllowed) {
      attributionCaptured = true;
      var attribution = readMarketingAttribution();
      Object.keys(attribution).forEach(function (key) { props[key] = attribution[key]; });
    }
    pushEvent("page_view", props);
    // One-shot deep-session milestone — fires exactly once per session when
    // crossing SESSION_MILESTONE_PV pageviews. Useful for rules that key off
    // "engaged session" without waiting for nightly analyses.
    try {
      var pvKey = sessionScopedKey(SESSION_PV_KEY);
      var milestoneKey = sessionScopedKey(SESSION_MILESTONE_KEY);
      var pv = parseInt(safeGet(sessionStorage, pvKey) || "0", 10) || 0;
      pv++;
      safeSet(sessionStorage, pvKey, String(pv));
      if (pv >= SESSION_MILESTONE_PV && !safeGet(sessionStorage, milestoneKey)) {
        safeSet(sessionStorage, milestoneKey, "1");
        pushEvent("deep_session_milestone", { page_views: pv });
      }
    } catch (e) {
      // The page-view event was already queued; session storage only tracks the optional milestone.
      return;
    }
  }

  function identifyCustomer() {
    if (!marketingAllowed || !cfg.hasCustomer || customerIdentified || !loadVisitor()) return;
    customerIdentified = true;
    if (cfg.customerEmail) identify("EMAIL", cfg.customerEmail);
    if (cfg.customerId) {
      var cid = String(cfg.customerId).replace(/[^0-9]/g, "");
      if (cid) identify("SHOPIFY_CUSTOMER", cid);
    }
  }

  function trackPdpDwellOnUnload() {
    var pageType = cfg.pageType || "";
    if (pageType !== "product") return;
    window.addEventListener("beforeunload", function () {
      if (!analyticsAllowed || !pageOpenedAt) return;
      pushEvent("pdp_dwell", {
        dwell_ms: Date.now() - pageOpenedAt,
        product_id: cfg.productId || undefined
      });
      flush(true);
    });
  }

  function interceptAddToCart() {
    var origFetch = window.fetch;
    if (typeof origFetch !== "function") return;
    window.fetch = function (input, init) {
      var url = typeof input === "string" ? input : (input && input.url) || "";
      var method = (init && init.method) || (typeof input !== "string" && input.method) || "GET";
      var promise = origFetch.apply(this, arguments);
      if (/\/cart\/add(\.js)?(\?|$)/.test(url) && /post/i.test(method)) {
        promise.then(function () {
          try { pushEvent("add_to_cart", { product_id: cfg.productId || undefined }); } catch (e) {
            // Optional analytics must not turn a successful cart response into a page error.
          }
        }, function () {
          // Failed cart requests are not successful add-to-cart events.
        });
      }
      return promise;
    };
  }

  // ---- expose API + boot ----------------------------------------------
  window.Auriq = {
    track: function (type, props) { try { pushEvent(type, props); } catch (e) {
      // The storefront may call this from purchase controls; instrumentation stays fail-open.
    } },
    identify: function (kind, value) { try { identify(kind, value); } catch (e) {
      // Marketing identity is optional and must not interrupt shopping.
    } },
    flush: function () { try { flush(false); } catch (e) {
      // A failed analytics flush is retried by the queued batch when possible.
    } },
    ensureGuestIdentity: ensureGuestIdentity,
    analyticsConsentGranted: function () { return analyticsAllowed; },
    concierge: null
  };

  function showTestSessionLabel() {
    var test = testSessionState();
    if (!test.present || !document.body || document.getElementById("auriq-test-session-label")) return;
    var label = document.createElement("div");
    label.id = "auriq-test-session-label";
    label.setAttribute("role", "status");
    label.textContent = test.session ? "TEST SESSION" : "TEST SESSION UNAVAILABLE";
    label.style.cssText = "position:fixed;top:calc(8px + env(safe-area-inset-top, 0px));right:8px;z-index:2147483645;padding:4px 7px;border:1px solid #0b3b24;border-radius:3px;background:#e8f3ec;color:#0b3b24;font:600 11px/1.3 system-ui,sans-serif;letter-spacing:.05em;pointer-events:none";
    document.body.appendChild(label);
  }

  function startBehavior(captureAttribution) {
    if (!analyticsAllowed) return;
    trackPageView(captureAttribution);
    pageOpenedAt = cfg.pageType === "product" ? Date.now() : 0;
    flush(false);
    loadProbes();
  }

  function analyticsConsent() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      return !!(privacy && typeof privacy.analyticsProcessingAllowed === "function" && privacy.analyticsProcessingAllowed());
    } catch (e) { return false; }
  }

  function marketingConsent() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      return !!(privacy && typeof privacy.marketingAllowed === "function" && privacy.marketingAllowed());
    } catch (e) { return false; }
  }

  function updateConsent() {
    var first = !privacyInitialized;
    var hadAnalytics = analyticsAllowed;
    var hadMarketing = marketingAllowed;
    privacyInitialized = true;
    // Operational design identity remains available; optional research awaits merchant authorization.
    analyticsAllowed = cfg.optionalResearchApproved === true && analyticsConsent();
    marketingAllowed = analyticsAllowed && marketingConsent();

    if (!analyticsAllowed) {
      behaviorActive = false;
      pageOpenedAt = 0;
      probesRequested = false;
      customerIdentified = false;
      var concierge = window.Auriq && window.Auriq.concierge;
      if (concierge && typeof concierge.withdraw === "function") {
        try { concierge.withdraw(); } catch (e) {
          // Keep clearing tracker state even if the optional widget cannot abort its request.
        }
      }
      clearBehavioralState();
      return;
    }

    if (!behaviorActive || !hadAnalytics) {
      behaviorActive = true;
      startBehavior(first && marketingAllowed);
    }
    if (marketingAllowed && !hadMarketing) {
      trackMarketingAttributionOnce();
      identifyCustomer();
    }
    if (hadMarketing && !marketingAllowed) {
      customerIdentified = false;
      queue = queue.map(function (event) {
        if (event.type !== "page_view") return event;
        var props = Object.assign({}, event.props);
        ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "gbraid", "wbraid"].forEach(function (key) { delete props[key]; });
        event.props = props;
        return event;
      });
    }
    initializeConsentFeatures();
  }

  function initializeConsentFeatures() {
    if (!analyticsAllowed) return;
    loadProbes();
    identifyCustomer();
    var concierge = window.Auriq && window.Auriq.concierge;
    if (concierge && typeof concierge.init === "function") {
      try { concierge.init(); } catch (e) {
        // Optional help availability must not block consented shopping behavior.
      }
    }
  }

  function initPrivacy() {
    document.addEventListener("visitorConsentCollected", updateConsent);
    var shopify = window.Shopify;
    if (!shopify || typeof shopify.loadFeatures !== "function") {
      updateConsent();
      return;
    }
    shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.2" }], function () {
      updateConsent();
    });
  }

  initPrivacy();
  showTestSessionLabel();
  trackPdpDwellOnUnload();
  interceptAddToCart();

  // Last-chance flush on pagehide; beacon survives the unload.
  window.addEventListener("pagehide", function () { flush(true); });

  // ---- probes (in-page micro-survey) ---------------------------------
  function probeSeenWithinCooldown(probeId, cooldownMinutes) {
    if (!probeId || !cooldownMinutes || cooldownMinutes <= 0) return false;
    var test = testSessionState();
    var store = test.present ? sessionStorage : localStorage;
    var key = test.present
      ? (TEST_PROBE_SEEN_PREFIX + (test.session ? test.session.sessionId : "unavailable") + "." + probeId)
      : PROBE_SEEN_PREFIX + probeId;
    var raw = safeGet(store, key);
    if (!raw) return false;
    var seenAt = parseInt(raw, 10);
    if (!isFinite(seenAt)) return false;
    return Date.now() - seenAt < cooldownMinutes * 60000;
  }

  function markProbeSeen(probeId) {
    if (!probeId) return;
    var test = testSessionState();
    var store = test.present ? sessionStorage : localStorage;
    var key = test.present
      ? (TEST_PROBE_SEEN_PREFIX + (test.session ? test.session.sessionId : "unavailable") + "." + probeId)
      : PROBE_SEEN_PREFIX + probeId;
    safeSet(store, key, String(Date.now()));
  }

  // Styles mirror auriq-concierge.js so both widgets read as one product
  // (Georgia serif, dark primary button, #fff background, !important locks
  // against theme CSS). Bottom-left placement keeps us off the concierge FAB.
  var PROBE_BOX_CSS = "position:fixed!important;left:16px!important;bottom:16px!important;width:320px!important;max-width:calc(100vw - 32px)!important;background:#fff!important;border:1px solid #ddd!important;border-radius:10px!important;box-shadow:0 8px 24px rgba(0,0,0,.12)!important;font-family:Georgia,serif!important;color:#111!important;padding:14px 16px!important;z-index:2147483646!important;";
  var PROBE_PROMPT_CSS = "font-size:14px!important;font-weight:600!important;line-height:1.4!important;color:#111!important;letter-spacing:0.04em!important;margin-bottom:12px!important;";
  var PROBE_CONTROLS_CSS = "display:flex!important;flex-wrap:wrap!important;gap:8px!important;";
  var PROBE_OPTION_BTN_CSS = "padding:8px 14px!important;border:1px solid #222!important;background:#fff!important;color:#111!important;border-radius:4px!important;cursor:pointer!important;font-family:Georgia,serif!important;font-size:13px!important;line-height:1!important;";
  var PROBE_PRIMARY_BTN_CSS = "padding:8px 14px!important;border:1px solid #222!important;background:#222!important;color:#fff!important;border-radius:4px!important;cursor:pointer!important;font-family:Georgia,serif!important;font-size:13px!important;font-weight:500!important;";
  var PROBE_INPUT_CSS = "flex:1!important;min-width:0!important;padding:8px 10px!important;border:1px solid #ccc!important;border-radius:4px!important;font-family:Georgia,serif!important;font-size:13px!important;background:#fff!important;color:#111!important;outline:none!important;";
  var PROBE_DISMISS_CSS = "display:inline-block!important;margin-top:10px!important;font-family:Georgia,serif!important;font-size:11px!important;color:#888!important;cursor:pointer!important;letter-spacing:0.04em!important;";

  function makeProbeOptionButton(label, onClick) {
    var b = document.createElement("button");
    b.type = "button";
    b.textContent = label;
    b.style.cssText = PROBE_OPTION_BTN_CSS;
    b.onclick = onClick;
    return b;
  }

  function renderProbe(probe) {
    if (!analyticsAllowed) return;
    var box = document.createElement("div");
    box.setAttribute("data-auriq-probe", probe.id);
    box.style.cssText = PROBE_BOX_CSS;

    var prompt = document.createElement("div");
    prompt.style.cssText = PROBE_PROMPT_CSS;
    prompt.textContent = probe.prompt;
    box.appendChild(prompt);

    var controls = document.createElement("div");
    controls.style.cssText = PROBE_CONTROLS_CSS;

    function close() { if (box.parentNode) box.parentNode.removeChild(box); }
    function submit(answer) {
      if (!analyticsAllowed) { close(); return; }
      var visitor = loadVisitor();
      if (!visitor) { close(); return; }
      fetch(cfg.proxyUrl + "/probe-response", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          visitor_id: visitor.id, visitor_hmac: visitor.hmac,
          probe_id: probe.id, answer: String(answer).slice(0, 512)
        })
      }).catch(function () {});
      close();
    }

    if (probe.kind === "YES_NO") {
      ["Yes", "No"].forEach(function (a) {
        controls.appendChild(makeProbeOptionButton(a, function () { submit(a); }));
      });
    } else if (probe.kind === "ENUM" && Array.isArray(probe.enum_values)) {
      probe.enum_values.forEach(function (a) {
        controls.appendChild(makeProbeOptionButton(a, function () { submit(a); }));
      });
    } else if (probe.kind === "RATING_1_5") {
      [1, 2, 3, 4, 5].forEach(function (n) {
        controls.appendChild(makeProbeOptionButton(String(n), function () { submit(n); }));
      });
    } else {
      var input = document.createElement("input");
      input.type = "text";
      input.style.cssText = PROBE_INPUT_CSS;
      input.addEventListener("keydown", function (ev) {
        if (ev.key === "Enter" && input.value.trim()) submit(input.value.trim());
      });
      var send = document.createElement("button");
      send.type = "button";
      send.textContent = "Send";
      send.style.cssText = PROBE_PRIMARY_BTN_CSS;
      send.onclick = function () { if (input.value.trim()) submit(input.value.trim()); };
      controls.appendChild(input);
      controls.appendChild(send);
    }

    var dismiss = document.createElement("a");
    dismiss.textContent = "Dismiss";
    dismiss.style.cssText = PROBE_DISMISS_CSS;
    dismiss.onclick = function () {
      try { pushEvent("probe_dismissed", { probe_id: probe.id }); } catch (e) {
        // Dismissing the optional survey must work even if event collection fails.
      }
      close();
    };

    box.appendChild(controls);
    box.appendChild(dismiss);
    // probe_shown fires through the regular event batcher; ingest.ts converts
    // it into a probe_impressions row (server-side cooldown source of truth).
    try { pushEvent("probe_shown", { probe_id: probe.id }); } catch (e) {
      // Showing an already-consented survey does not depend on analytics delivery.
    }
    markProbeSeen(probe.id);
    document.body.appendChild(box);
  }

  function loadProbes() {
    if (!analyticsAllowed || probesRequested) return;
    var visitor = loadVisitor();
    if (!visitor) return;
    probesRequested = true;
    fetch(cfg.proxyUrl + "/probes", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        context: cfg.pageType || "",
        visitor_id: visitor.id,
        visitor_hmac: visitor.hmac,
        session_id: sessionId(),
        page_url: window.location.pathname
      })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!analyticsAllowed || !data || !Array.isArray(data.probes) || data.probes.length === 0) return;
        var probe = data.probes[0];
        if (!probe || !probe.id) return;
        // Belt-and-suspenders: server already filtered by impression cooldown,
        // but if the impression event was lost or hasn't landed yet we still
        // honor the local seen-at marker.
        if (probeSeenWithinCooldown(probe.id, probe.cooldown_minutes)) return;
        setTimeout(function () { renderProbe(probe); }, 4000);
      })
      .catch(function () {});
  }

  // Bootstrap concierge if its asset loaded alongside us.
  setTimeout(function () {
    if (window.Auriq && window.Auriq.concierge && typeof window.Auriq.concierge.init === "function") {
      try { window.Auriq.concierge.init(); } catch (e) {
        // Optional concierge availability cannot interrupt storefront behavior.
      }
    }
  }, 3000);
})();
